import { X, Download, Shapes } from 'lucide-react';
import toast from 'react-hot-toast';

// Shows a pictogram image from the GPU pictogram service, with any required attribution
// (e.g. "Servier Medical Art by Servier, licensed under CC BY 4.0") in small text below.
export default function PictogramModal({ data, onClose }) {
  if (!data) return null;
  const { image, attributions = [] } = data;

  const handleDownload = async () => {
    try {
      const blob = await (await fetch(image)).blob();
      const file = new File([blob], `pictogram-${data.id || Date.now()}.png`, { type: 'image/png' });
      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (isMobile && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: 'Pictogram' });
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      if (err?.name === 'AbortError') return; // user cancelled the share sheet
      window.open(image, '_blank');           // WebViews without download support
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-auto shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Shapes className="w-5 h-5 mr-2 text-pink-600" /> Pictogram
          </h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleDownload().catch(() => toast.error('Download failed'))}
              className="flex items-center px-3 py-1.5 text-sm bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
            >
              <Download className="w-4 h-4 mr-1" /> Download
            </button>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg" aria-label="Close">
              <X className="w-5 h-5 text-gray-700" />
            </button>
          </div>
        </div>
        <div className="p-4">
          <img src={image} alt="Pictogram" className="w-full h-auto rounded-lg border border-gray-200" />
          {attributions.length > 0 && (
            <div className="mt-2 space-y-0.5">
              {attributions.map((a, i) => (
                <p key={i} className="text-[11px] leading-snug text-gray-500 text-center">{a}</p>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
